// Renders a guest review's per-category sub-ratings as a compact summary
// ("Cleanliness 5 · Communication 4"): known categories (Airbnb's taxonomy,
// largely shared by VRBO/Booking.com) first in canonical display order, then any
// platform-specific extras with a humanized label so none are silently dropped.
// 'overall' is excluded — it's shown as the main rating pill. (Trello 18593.)

export interface SubRatings {
  [category: string]: number;
}

const KNOWN_LABELS: [string, string][] = [
  ['cleanliness', 'Cleanliness'],
  ['accuracy', 'Accuracy'],
  ['checkIn', 'Check-in'],
  ['communication', 'Communication'],
  ['location', 'Location'],
  ['value', 'Value'],
];

const CANONICAL_ORDER: string[] = KNOWN_LABELS.map(([key]) => key);
const LABEL_BY_KEY: Record<string, string> = Object.fromEntries(KNOWN_LABELS);

// Display label for a category: the known label, else a humanized fallback for a
// platform-specific category ('propertyCondition'/'property_condition' → 'Property condition').
export function subRatingLabel(key: string): string {
  const known = LABEL_BY_KEY[key];
  if (known) {
    return known;
  }
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key;
}

export function buildSubRatingsSummary(ratings: SubRatings | null | undefined): string {
  if (!ratings) {
    return '';
  }
  const keys = Object.keys(ratings).filter(key => key !== 'overall' && typeof ratings[key] === 'number');
  const ordered = [
    ...CANONICAL_ORDER.filter(key => keys.includes(key)),
    ...keys.filter(key => !CANONICAL_ORDER.includes(key)),
  ];
  return ordered.map(key => `${subRatingLabel(key)} ${ratings[key]}`).join(' · ');
}
